import React, { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import relativeTime from "dayjs/plugin/relativeTime";
import { useHistory, Prompt, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatAppNumber } from "util/locale";
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Progress,
  Select,
  Cascader,
  Skeleton,
  Spin,
  Switch,
  Tag,
  Tooltip,
  DatePicker,
  notification,
  message,
} from "antd";
import {
  SearchOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  CheckSquareOutlined,
  CloseSquareOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  HomeOutlined,
  RightOutlined,
  BulbOutlined,
  FileTextOutlined,
  RiseOutlined,
  HighlightOutlined,
  RadarChartOutlined,
  FireOutlined,
  MessageOutlined,
  AimOutlined,
  TeamOutlined,
  LoadingOutlined,
  SmileOutlined,
  LikeOutlined,
} from "@ant-design/icons";
import { apiRequest } from "util/services";
import { timezoneList } from "util/constants";
import styles from "./style.module.scss";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;

const PAGE_SIZE = 20;

/** GenAI narrative/table metrics that count toward monthly AI credits (not KPI charts). */
const AI_CREDIT_METRIC_IDS = new Set([20, 21, 23, 25]);

/** Same labels as My Workspace; topic KPIs grouped after Decision / Hot Terms. */
const AI_METRIC_DISPLAY_ORDER = [21, 20, 23, 25, 27, 28, 10, 29, 14, 15, 12];

function sortAiMetricsForDisplay(metrics) {
  const rank = (id) => {
    const i = AI_METRIC_DISPLAY_ORDER.indexOf(Number(id));
    return i === -1 ? 999 : i;
  };
  return [...(metrics || [])].sort((a, b) => rank(a.id) - rank(b.id) || Number(a.id) - Number(b.id));
}

function aiMetricCardLabel(metric, t) {
  const id = Number(metric?.id);
  if (!Number.isFinite(id)) return metric?.name || "";
  const key = `welcome.metricName_${id}`;
  const label = t(key);
  return label !== key ? label : metric.name || "";
}

/** Icons aligned with My Workspace brand metric picker (Welcome). */
const AI_MONTHLY_METRIC_ICON_BY_ID = {
  12: MessageOutlined,
  14: AimOutlined,
  15: TeamOutlined,
  20: BulbOutlined,
  21: FileTextOutlined,
  23: RiseOutlined,
  25: HighlightOutlined,
  27: RadarChartOutlined,
  28: FireOutlined,
  10: SmileOutlined,
  29: LikeOutlined,
};

function AiMonthlyMetricIcon({ metricId, className }) {
  const Comp = AI_MONTHLY_METRIC_ICON_BY_ID[Number(metricId)] || InfoCircleOutlined;
  return <Comp className={className} aria-hidden />;
}

/**
 * Aylık tekrarlayan koşu: API worker ile aynı veri ayı.
 * - Gün 1–30: koşu ayından bir önceki takvim ayının tamamı (13 Haziran → Mayıs 1–31).
 * - “Son gün” (31): o ayın sonunda koşar; kapsam o ayın kendisi.
 */
function getNextAiRefreshCoverMonthStart(
  userIanaTz,
  scheduledDay,
  scheduledHour,
  scheduledMinute,
) {
  const z = userIanaTz || "UTC";
  const localNow = dayjs().tz(z);
  const runClock = dayjs
    .utc()
    .hour(scheduledHour)
    .minute(scheduledMinute)
    .second(0)
    .tz(z);
  const h = runClock.hour();
  const mi = runClock.minute();

  const atLastDayOfMonth = (base) => {
    const lastD = base.daysInMonth();
    return base.startOf("month").date(lastD).hour(h).minute(mi).second(0);
  };

  const atDayNOfMonth = (base) => {
    const dim = base.daysInMonth();
    const d = Math.min(scheduledDay, dim);
    return base.startOf("month").date(d).hour(h).minute(mi).second(0);
  };

  let thisMonthRun;
  if (scheduledDay === 31) {
    thisMonthRun = atLastDayOfMonth(localNow);
  } else {
    thisMonthRun = atDayNOfMonth(localNow);
  }

  let nextRun;
  if (thisMonthRun.isAfter(localNow)) {
    nextRun = thisMonthRun;
  } else if (scheduledDay === 31) {
    nextRun = atLastDayOfMonth(localNow.add(1, "month"));
  } else {
    nextRun = atDayNOfMonth(localNow.add(1, "month"));
  }

  if (scheduledDay === 31) {
    return nextRun.startOf("month");
  }
  return nextRun.startOf("month").subtract(1, "month");
}

const AiAutoRefreshSettings = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  // loading: blocks full page (only for fast config call)
  // pivotOptionsLoading: only blocks vendor list section
  const [loading, setLoading] = useState(true);
  const [pivotOptionsLoading, setPivotOptionsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // Snapshot of last-saved values to compare against
  const savedSnapshot = React.useRef(null);

  const [quota, setQuota] = useState(null);
  /** Mirrors backend user_can_edit_workspace_content; false = read-only org member. */
  const [settingsEditable, setSettingsEditable] = useState(true);
  const [pivotOptions, setPivotOptions] = useState({});
  const [availableMetrics, setAvailableMetrics] = useState([]);
  const [availableTopics, setAvailableTopics] = useState([]);

  const [selectedPivotKey, setSelectedPivotKey] = useState(null);
  const [selectedVendorNames, setSelectedVendorNames] = useState([]);
  const [selectedMetricIds, setSelectedMetricIds] = useState([]);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [payPerUseEnabled, setPayPerUseEnabled] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [scheduledDay, setScheduledDay] = useState(1);
  const [scheduledHour, setScheduledHour] = useState(0);
  const [scheduledMinute, setScheduledMinute] = useState(0);
  const [scheduleMode, setScheduleMode] = useState("monthly");
  const [oneTimeSince, setOneTimeSince] = useState(null);
  const [oneTimeUntil, setOneTimeUntil] = useState(null);
  const [oneTimeRunDate, setOneTimeRunDate] = useState(null);
  const [oneTimePending, setOneTimePending] = useState(false);
  // IANA timezone resolved from user preference (e.g. "Europe/Istanbul")
  const [userIanaTz, setUserIanaTz] = useState("UTC");
  const [lastRunStatus, setLastRunStatus] = useState(null);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [lastRunMonth, setLastRunMonth] = useState(null);

  const [brandGroups, setBrandGroups] = useState([]);
  /** Dashboards for selected competitive brand (POST /welcome/dashboards) */
  const [scopedDashboards, setScopedDashboards] = useState([]);
  /** Lazy-loaded tree: group → dashboards (nested dropdown) */
  const [cascaderOptions, setCascaderOptions] = useState([]);
  const [selectedBrandGroupId, setSelectedBrandGroupId] = useState(null);
  const [selectedSourceDashboardId, setSelectedSourceDashboardId] = useState(null);

  const [vendorSearch, setVendorSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const sortedMetrics = useMemo(() => sortAiMetricsForDisplay(availableMetrics), [availableMetrics]);

  useEffect(() => {
    fetchConfigBase();
  }, []);

  /** Pivot vendor lists; pass dashboard + competitive brand group id (same query as Welcome widget scope). */
  const fetchPivotOptions = async (dashboardId, brandGroupId, opts = {}) => {
    const { silent = false } = opts;
    if (!silent) {
      setPivotOptionsLoading(true);
    }
    try {
      const params = new URLSearchParams();
      if (dashboardId != null && Number.isFinite(Number(dashboardId))) {
        params.set("dashboard_id", String(dashboardId));
      }
      if (brandGroupId != null && Number.isFinite(Number(brandGroupId))) {
        params.set("brand_group_id", String(brandGroupId));
      }
      const qs = params.toString();
      const res = await apiRequest(
        `welcome/ai-services/pivot-options${qs ? `?${qs}` : ""}`,
        "GET"
      );
      if (res?.success) {
        const optsOut = res.success.pivot_options || {};
        setPivotOptions(optsOut);
        return optsOut;
      }
    } catch (e) {
      notification.error({ message: t("ai_services.load_error") });
    } finally {
      if (!silent) {
        setPivotOptionsLoading(false);
      }
    }
    return {};
  };

  /**
   * Same as Welcome → new widget wizard → fetchBrands(): GET welcome/brands
   */
  const fetchWizardBrandList = async () => {
    try {
      const response = await apiRequest("welcome/brands", "GET");
      if (response?.success && Array.isArray(response.success)) {
        return response.success;
      }
      notification.error({
        message: t("common.error"),
        description: t("welcome.errorFetchBrands"),
      });
      return [];
    } catch (e) {
      notification.error({
        message: t("common.error"),
        description: t("welcome.errorFetchBrands"),
      });
      return [];
    }
  };

  /** POST /welcome/dashboards — returns rows only (no React state). */
  const fetchDashboardsForBrandOnly = async (brandId) => {
    if (!Number.isFinite(Number(brandId))) {
      return [];
    }
    try {
      const response = await apiRequest(
        "welcome/dashboards",
        "POST",
        { brand_ids: [Number(brandId)] },
        true,
      );
      if (response?.success) {
        const rows = response.success.map((d) => ({
          id: d.id,
          name: d.name != null ? String(d.name).trim() : "",
        }));
        rows.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        return rows;
      }
      notification.error({
        message: t("common.error"),
        description: t("welcome.errorFetchDashboards"),
      });
    } catch (e) {
      notification.error({
        message: t("common.error"),
        description: t("welcome.errorFetchDashboards"),
      });
    }
    return [];
  };

  const loadDashboardsForBrand = async (brandId) => {
    const rows = await fetchDashboardsForBrandOnly(brandId);
    setScopedDashboards(rows);
    return rows;
  };

  const groupsToCascaderParents = (groups) =>
    groups
      .map((g) => ({
        value: Number(g.groupId),
        label: `${g.name} (${g.dashboardCount} ${t("ai_services.dashboards")})`,
        isLeaf: false,
      }))
      .filter((o) => Number.isFinite(o.value));

  const loadCascaderData = async (selectedOptions) => {
    const targetOption = selectedOptions[selectedOptions.length - 1];
    const gid = targetOption.value;
    if (targetOption.children?.length) {
      return;
    }
    setCascaderOptions((opts) =>
      opts.map((o) => (Number(o.value) === Number(gid) ? { ...o, loading: true } : o)),
    );

    const rows = await fetchDashboardsForBrandOnly(gid);

    setCascaderOptions((opts) =>
      opts.map((o) =>
        Number(o.value) === Number(gid)
          ? {
              ...o,
              loading: false,
              children: rows.map((d) => ({
                value: d.id,
                label: d.name,
                isLeaf: true,
              })),
            }
          : o,
      ),
    );
  };

  /** Same as Welcome wizard: POST welcome/topics with dashboard_ids after scope is chosen. */
  const loadTopicsForDashboards = async (dashboardIds) => {
    if (!Array.isArray(dashboardIds) || dashboardIds.length === 0) {
      setAvailableTopics([]);
      return [];
    }
    try {
      const response = await apiRequest(
        "welcome/topics",
        "POST",
        { dashboard_ids: dashboardIds.map((d) => Number(d)).filter((n) => Number.isFinite(n)) },
        true,
      );
      if (response?.success && Array.isArray(response.success)) {
        const topics = response.success.map((row) => ({
          id: row.categoryId,
          name: (row.name && String(row.name)) || "",
        }));
        topics.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setAvailableTopics(topics);
        return topics;
      }
    } catch (e) {
      notification.error({ message: t("ai_services.load_error") });
    }
    setAvailableTopics([]);
    return [];
  };

  const handleSourceCascaderChange = async (value) => {
    setIsDirty(true);
    if (!value || !Array.isArray(value) || value.length === 0) {
      setSelectedBrandGroupId(null);
      setSelectedSourceDashboardId(null);
      setAvailableTopics([]);
      setSelectedTopicIds([]);
      setSelectedPivotKey(null);
      setSelectedVendorNames([]);
      setVendorSearch("");
      setCurrentPage(1);
      setScopedDashboards([]);
      await fetchPivotOptions(null, null);
      return;
    }
    if (value.length < 2) {
      return;
    }
    const gid = Number(value[0]);
    const did = Number(value[1]);
    if (!Number.isFinite(gid) || !Number.isFinite(did)) {
      return;
    }
    setPivotOptionsLoading(true);
    setSelectedBrandGroupId(gid);
    await loadDashboardsForBrand(gid);
    await handleScopedDashboardChange(did, gid);
  };

  const handleScopedDashboardChange = async (dashId, brandIdForPivot) => {
    const id = dashId != null ? Number(dashId) : null;
    const bid =
      brandIdForPivot != null ? Number(brandIdForPivot) : selectedBrandGroupId;
    setSelectedSourceDashboardId(Number.isFinite(id) ? id : null);
    setVendorSearch("");
    setCurrentPage(1);
    let opts = {};
    if (Number.isFinite(id)) {
      setPivotOptionsLoading(true);
      setSelectedTopicIds([]);
      // Pivot list does not depend on topics — run in parallel so slow /welcome/topics can't block pivots.
      const results = await Promise.all([
        loadTopicsForDashboards([id]),
        fetchPivotOptions(id, Number.isFinite(bid) ? bid : null),
      ]);
      opts = results[1] || {};
    } else {
      setAvailableTopics([]);
      setSelectedTopicIds([]);
      opts =
        (await fetchPivotOptions(null, Number.isFinite(bid) ? bid : null)) || {};
    }
    const keys = Object.keys(opts || {});
    if (selectedPivotKey && keys.length > 0 && !keys.includes(selectedPivotKey)) {
      setSelectedPivotKey(null);
      setSelectedVendorNames([]);
    }
  };

  // Fast call: config + metrics + quota (topics via POST /welcome/topics when dashboard known)
  // Load welcome/brands in parallel with config so group names appear even if config errors (504).
  const fetchConfigBase = async () => {
    setLoading(true);
    try {
      const [configRes, groups] = await Promise.all([
        apiRequest("welcome/ai-services/config?exclude_pivot_options=true", "GET"),
        fetchWizardBrandList(),
      ]);

      setBrandGroups(groups);
      const parentOpts = groupsToCascaderParents(groups);
      setCascaderOptions(parentOpts);

      if (!configRes || configRes.cancel) {
        notification.error({ message: t("ai_services.load_error") });
        setPivotOptionsLoading(false);
        return;
      }
      if (!configRes.success) {
        notification.error({
          message: t("ai_services.load_error_detail"),
        });
        setPivotOptionsLoading(false);
        return;
      }
      if (configRes?.success) {
        const { config, metrics, topics, quota: q } = configRes.success;
        const rawBrand =
          config.source_brand_group_id != null ? Number(config.source_brand_group_id) : null;
        const brandOk =
          rawBrand != null &&
          groups.some((g) => Number(g.groupId) === Number(rawBrand));
        setSelectedBrandGroupId(brandOk ? rawBrand : null);

        let dashRows = [];
        if (brandOk && rawBrand != null) {
          dashRows = await loadDashboardsForBrand(rawBrand);
        } else {
          setScopedDashboards([]);
        }

        let nextCascaderOpts = parentOpts;
        if (brandOk && rawBrand != null && dashRows.length > 0) {
          nextCascaderOpts = parentOpts.map((opt) =>
            Number(opt.value) === Number(rawBrand)
              ? {
                  ...opt,
                  children: dashRows.map((d) => ({
                    value: d.id,
                    label: d.name,
                    isLeaf: true,
                  })),
                }
              : opt,
          );
        }
        setCascaderOptions(nextCascaderOpts);
        const dashIdSet = new Set(dashRows.map((d) => d.id));
        const rawSrc = config.source_dashboard_id;
        const resolvedSourceId =
          rawSrc != null && dashIdSet.has(rawSrc) ? rawSrc : null;

        setAvailableMetrics(metrics || []);
        setAvailableTopics(topics || []);
        setQuota(q);
        setSettingsEditable(
          configRes.success.can_edit_auto_refresh_settings !== false,
        );
        setSelectedPivotKey(config.pivot_key || null);
        setSelectedVendorNames(config.vendor_names || []);
        setSelectedMetricIds(config.metric_ids || []);
        setSelectedTopicIds(config.topic_ids || []);
        setPayPerUseEnabled(config.pay_per_use_enabled || false);
        setIsActive(config.is_active !== undefined ? config.is_active : true);
        setScheduledDay(config.scheduled_day || 1);
        setScheduledHour(config.scheduled_hour || 0);
        setScheduledMinute(config.scheduled_minute || 0);
        setScheduleMode(config.schedule_mode || "monthly");
        setOneTimeSince(config.one_time_since || null);
        setOneTimeUntil(config.one_time_until || null);
        setOneTimeRunDate(config.one_time_run_date || null);
        setOneTimePending(Boolean(config.one_time_pending));
        setSelectedSourceDashboardId(resolvedSourceId);
        setLastRunStatus(config.last_run_status || null);
        setLastRunAt(config.last_run_at || null);
        setLastRunMonth(config.last_run_month || null);
        savedSnapshot.current = {
          pivot_key: config.pivot_key || null,
          vendor_names: JSON.stringify(config.vendor_names || []),
          metric_ids: JSON.stringify(config.metric_ids || []),
          topic_ids: JSON.stringify(config.topic_ids || []),
          is_active: config.is_active !== undefined ? config.is_active : true,
          scheduled_day: config.scheduled_day || 1,
          scheduled_hour: config.scheduled_hour || 0,
          scheduled_minute: config.scheduled_minute || 0,
          schedule_mode: config.schedule_mode || "monthly",
          one_time_since: config.one_time_since || null,
          one_time_until: config.one_time_until || null,
          one_time_run_date: config.one_time_run_date || null,
          one_time_pending: Boolean(config.one_time_pending),
          source_brand_group_id: brandOk ? rawBrand : null,
          source_dashboard_id: resolvedSourceId,
        };
        setIsDirty(false);
        const tzLabel = configRes.success.user_timezone_label || "";
        const ianaFromLabel = timezoneList[tzLabel] || (tzLabel.includes("/") ? tzLabel : null);
        setUserIanaTz(ianaFromLabel || "UTC");

        // Hydrate topics + pivot. `silent` pivot fetch skips setPivotOptionsLoading in finally —
        // always reset the banner here after hydrate (fixes endless pivot spinner).
        if (brandOk && resolvedSourceId != null) {
          await Promise.all([
            loadTopicsForDashboards([resolvedSourceId]),
            fetchPivotOptions(resolvedSourceId, rawBrand, { silent: true }),
          ]);
        } else if (!brandOk && resolvedSourceId == null && groups.length === 0) {
          await fetchPivotOptions(null, null, { silent: true });
        } else {
          setPivotOptions({});
        }
        setPivotOptionsLoading(false);
      }
    } catch (e) {
      notification.error({ message: t("ai_services.load_error") });
      setPivotOptionsLoading(false);
    } finally {
      setLoading(false);
    }
  };

  // List of vendors for the selected pivot key
  const allVendors = useMemo(() => {
    if (!selectedPivotKey) return [];
    return pivotOptions[selectedPivotKey] || [];
  }, [pivotOptions, selectedPivotKey]);

  const filteredVendors = useMemo(() => {
    const q = vendorSearch.trim().toLowerCase();
    const selectedSet = new Set(selectedVendorNames);

    const matchesQuery = (v) =>
      !q || (v.name && String(v.name).toLowerCase().includes(q));

    const filtered = allVendors.filter(matchesQuery);

    if (q) {
      const hiddenSelected = allVendors.filter(
        (v) => selectedSet.has(v.name) && !matchesQuery(v),
      );
      if (hiddenSelected.length === 0) {
        return filtered;
      }
      const tail = filtered.filter(
        (v) => !hiddenSelected.some((h) => h.name === v.name),
      );
      return [...hiddenSelected, ...tail];
    }

    if (selectedVendorNames.length === 0) {
      return filtered;
    }

    const selectedRows = [];
    const otherRows = [];
    for (const v of filtered) {
      if (selectedSet.has(v.name)) {
        selectedRows.push(v);
      } else {
        otherRows.push(v);
      }
    }
    selectedRows.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    otherRows.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    return [...selectedRows, ...otherRows];
  }, [allVendors, vendorSearch, selectedVendorNames]);

  const pagedVendors = useMemo(() => {
    return filteredVendors.slice(0, currentPage * PAGE_SIZE);
  }, [filteredVendors, currentPage]);

  const pivotKeyOptions = useMemo(
    () =>
      Object.keys(pivotOptions).map((k) => ({
        value: k,
        label: `${k} (${pivotOptions[k].length} ${t("ai_services.values")})`,
      })),
    [pivotOptions, t]
  );

  const cascaderValue =
    selectedBrandGroupId != null && selectedSourceDashboardId != null
      ? [selectedBrandGroupId, selectedSourceDashboardId]
      : undefined;

  const handlePivotKeyChange = (key) => {
    setSelectedPivotKey(key);
    setSelectedVendorNames([]);
    setVendorSearch("");
    setCurrentPage(1);
  };

  const handleSelectAllVendors = () => {
    setSelectedVendorNames(filteredVendors.map((v) => v.name));
  };

  const handleClearVendors = () => {
    setSelectedVendorNames([]);
  };

  const handleVendorToggle = (vendorName, checked) => {
    setSelectedVendorNames((prev) =>
      checked ? [...prev, vendorName] : prev.filter((n) => n !== vendorName)
    );
  };

  const handleMetricToggle = (metricId, checked) => {
    setSelectedMetricIds((prev) =>
      checked ? [...prev, metricId] : prev.filter((id) => id !== metricId)
    );
  };

  const handleTopicToggle = (topicId, checked) => {
    setSelectedTopicIds((prev) =>
      checked ? [...prev, topicId] : prev.filter((id) => id !== topicId)
    );
  };

  const billableMetricCount = useMemo(
    () => selectedMetricIds.filter((id) => AI_CREDIT_METRIC_IDS.has(Number(id))).length,
    [selectedMetricIds],
  );

  const creditsPerMonth = selectedVendorNames.length * billableMetricCount;

  const scheduleTimeLabel = useMemo(
    () =>
      dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).format("HH:mm"),
    [scheduledHour, scheduledMinute, userIanaTz],
  );

  const scheduleCoveragePreview = useMemo(() => {
    const coverMonthStart = getNextAiRefreshCoverMonthStart(
      userIanaTz,
      scheduledDay,
      scheduledHour,
      scheduledMinute,
    );
    return {
      prevMonthStart: coverMonthStart.format("D MMMM"),
      prevMonthEnd: coverMonthStart.endOf("month").format("D MMMM YYYY"),
    };
  }, [userIanaTz, scheduledDay, scheduledHour, scheduledMinute]);

  const formDisabled = loading || !settingsEditable;

  const _doSave = async () => {
    if (!settingsEditable) {
      message.warning(t("ai_services.read_only_no_save"));
      return;
    }
    setSaving(true);
    try {
      const res = await apiRequest("welcome/ai-services/config", "POST", {
        pivot_key: selectedPivotKey,
        vendor_names: selectedVendorNames,
        metric_ids: selectedMetricIds,
        topic_ids: selectedTopicIds,
        pay_per_use_enabled: payPerUseEnabled,
        is_active: isActive,
        schedule_mode: scheduleMode,
        scheduled_day: scheduledDay,
        scheduled_hour: scheduledHour,
        scheduled_minute: scheduledMinute,
        one_time_since: scheduleMode === "one_time" ? oneTimeSince : null,
        one_time_until: scheduleMode === "one_time" ? oneTimeUntil : null,
        one_time_run_date: scheduleMode === "one_time" ? oneTimeRunDate : null,
        source_dashboard_id: selectedSourceDashboardId,
        source_brand_group_id: selectedBrandGroupId,
        workspace_group_label: null,
      });
      if (res?.success) {
        const saved = res.success;
        const rawBrand =
          saved.source_brand_group_id != null ? Number(saved.source_brand_group_id) : null;
        const brandAllowed =
          rawBrand != null &&
          brandGroups.some((g) => Number(g.groupId) === Number(rawBrand));
        let dashRows = scopedDashboards;
        if (brandAllowed && rawBrand != null) {
          dashRows = await loadDashboardsForBrand(rawBrand);
          setCascaderOptions((prev) => {
            const roots =
              prev.length > 0 ? prev : groupsToCascaderParents(brandGroups);
            return roots.map((opt) =>
              Number(opt.value) === Number(rawBrand)
                ? {
                    ...opt,
                    children: (dashRows || []).map((d) => ({
                      value: d.id,
                      label: d.name,
                      isLeaf: true,
                    })),
                  }
                : opt,
            );
          });
        }
        const dashIdSet = new Set((dashRows || []).map((d) => d.id));
        const rawSavedSrc = saved.source_dashboard_id;
        const resolvedSavedSrc =
          rawSavedSrc != null && dashIdSet.has(rawSavedSrc) ? rawSavedSrc : null;

        setSelectedPivotKey(saved.pivot_key || null);
        setSelectedVendorNames(saved.vendor_names || []);
        setSelectedMetricIds(saved.metric_ids || []);
        setSelectedTopicIds(saved.topic_ids || []);
        setScheduledDay(saved.scheduled_day || 1);
        setScheduledHour(saved.scheduled_hour || 0);
        setScheduledMinute(saved.scheduled_minute || 0);
        setScheduleMode(saved.schedule_mode || "monthly");
        setOneTimeSince(saved.one_time_since || null);
        setOneTimeUntil(saved.one_time_until || null);
        setOneTimeRunDate(saved.one_time_run_date || null);
        setOneTimePending(Boolean(saved.one_time_pending));
        setSelectedBrandGroupId(brandAllowed ? rawBrand : null);
        setSelectedSourceDashboardId(resolvedSavedSrc);
        savedSnapshot.current = {
          pivot_key: saved.pivot_key || null,
          vendor_names: JSON.stringify(saved.vendor_names || []),
          metric_ids: JSON.stringify(saved.metric_ids || []),
          topic_ids: JSON.stringify(saved.topic_ids || []),
          is_active: saved.is_active !== undefined ? saved.is_active : true,
          scheduled_day: saved.scheduled_day || 1,
          scheduled_hour: saved.scheduled_hour || 0,
          scheduled_minute: saved.scheduled_minute || 0,
          schedule_mode: saved.schedule_mode || "monthly",
          one_time_since: saved.one_time_since || null,
          one_time_until: saved.one_time_until || null,
          one_time_run_date: saved.one_time_run_date || null,
          one_time_pending: Boolean(saved.one_time_pending),
          source_brand_group_id: brandAllowed ? rawBrand : null,
          source_dashboard_id: resolvedSavedSrc,
        };
        setIsDirty(false);
        message.success(t("ai_services.save_success"));
      } else {
        message.error(
          typeof res?.error === "string" && res.error
            ? res.error
            : t("ai_services.save_error"),
        );
      }
    } catch (e) {
      message.error(t("ai_services.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!settingsEditable) {
      message.warning(t("ai_services.read_only_no_save"));
      return;
    }
    if (brandGroups.length > 0 && (!selectedBrandGroupId || !selectedSourceDashboardId)) {
      message.warning(t("ai_services.save_validation_brand_dashboard"));
      return;
    }
    const pivotKeyOk = selectedPivotKey && String(selectedPivotKey).trim().length > 0;
    const vendorsOk =
      Array.isArray(selectedVendorNames) && selectedVendorNames.length > 0;
    if (!pivotKeyOk || !vendorsOk) {
      message.warning(t("ai_services.save_validation_pivot"));
      return;
    }
    if (!selectedMetricIds.length) {
      message.warning(t("ai_services.save_validation_metrics"));
      return;
    }
    if (availableTopics.length > 0 && !selectedTopicIds.length) {
      message.warning(t("ai_services.save_validation_topics"));
      return;
    }
    if (scheduleMode === "one_time") {
      if (!oneTimeSince || !oneTimeUntil || !oneTimeRunDate) {
        message.warning(t("ai_services.save_validation_one_time_dates"));
        return;
      }
      if (dayjs(oneTimeSince).isAfter(dayjs(oneTimeUntil), "day")) {
        message.warning(t("ai_services.save_validation_one_time_range"));
        return;
      }
    }

    // If vendor/topic/pivot config changed, check for existing reports this month
    const s = savedSnapshot.current || {};
    const vendorsChanged =
      JSON.stringify([...(selectedVendorNames || [])].sort()) !==
      JSON.stringify(JSON.parse(s.vendor_names || "[]").sort());
    const topicsChanged =
      JSON.stringify([...(selectedTopicIds || [])].sort()) !==
      JSON.stringify(JSON.parse(s.topic_ids || "[]").sort());
    const pivotChanged = selectedPivotKey !== s.pivot_key;
    const brandDashChanged =
      selectedBrandGroupId !== s.source_brand_group_id ||
      selectedSourceDashboardId !== s.source_dashboard_id;

    if (vendorsChanged || topicsChanged || pivotChanged || brandDashChanged) {
      try {
        const currentMonth = dayjs().format("YYYY-MM");
        const checkRes = await apiRequest(
          `welcome/ai-services/reports?month=${currentMonth}&limit=50`,
          "GET"
        );
        const existingReports = (checkRes?.success?.reports || []).filter(
          (r) => r.status !== "failed"
        );
        if (existingReports.length > 0) {
          const names = [...new Set(existingReports.map((r) => r.brand_name))].join(", ");
          Modal.confirm({
            title: t("ai_services.save_conflict_title"),
            content: t("ai_services.save_conflict_desc", { names, month: dayjs().format("MMMM YYYY") }),
            okText: t("ai_services.save_anyway"),
            cancelText: t("ai_services.go_to_reports"),
            okType: "default",
            onOk: () => _doSave(),
            onCancel: () => history.push("/console/ai-services/auto-reports"),
          });
          return;
        }
      } catch (_) {
        // If check fails, proceed with save silently
      }
    }
    await _doSave();
  };


  useEffect(() => {
    if (!savedSnapshot.current || loading) return;
    const s = savedSnapshot.current;
    const changed =
      selectedPivotKey !== s.pivot_key ||
      JSON.stringify(selectedVendorNames) !== s.vendor_names ||
      JSON.stringify(selectedMetricIds) !== s.metric_ids ||
      JSON.stringify(selectedTopicIds) !== s.topic_ids ||
      isActive !== s.is_active ||
      scheduledDay !== s.scheduled_day ||
      scheduledHour !== s.scheduled_hour ||
      scheduledMinute !== s.scheduled_minute ||
      scheduleMode !== (s.schedule_mode || "monthly") ||
      oneTimeSince !== s.one_time_since ||
      oneTimeUntil !== s.one_time_until ||
      oneTimeRunDate !== s.one_time_run_date ||
      selectedBrandGroupId !== s.source_brand_group_id ||
      selectedSourceDashboardId !== s.source_dashboard_id;
    setIsDirty(changed);
  }, [selectedPivotKey, selectedVendorNames, selectedMetricIds, selectedTopicIds,
      isActive, scheduledDay, scheduledHour, scheduledMinute, scheduleMode,
      oneTimeSince, oneTimeUntil, oneTimeRunDate, selectedBrandGroupId,
      selectedSourceDashboardId, loading]);

  const quotaPercent = quota
    ? Math.min(100, Math.round((quota.used / quota.total) * 100))
    : 0;
  const quotaOverBudget = quota && creditsPerMonth > quota.remaining;

  // Warn on browser tab/window close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  return (
    <div className={styles.page}>
      {/* Warn on in-app navigation when there are unsaved changes */}
      <Prompt
        when={isDirty && settingsEditable}
        message={t("ai_services.unsaved_changes_warning")}
      />
      {!loading && !settingsEditable && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("ai_services.auto_refresh_read_only_banner")}
        />
      )}
      {/* Header */}
      <div className={styles.pageHeader}>
        <p className={styles.pageTitle}>{t("ai_services.auto_refresh_title")}</p>
        {loading ? (
          <Skeleton.Button active size="small" style={{ width: 80 }} />
        ) : (
          <Switch
            checked={isActive}
            onChange={setIsActive}
            checkedChildren={t("ai_services.active")}
            unCheckedChildren={t("ai_services.paused")}
            disabled={formDisabled}
          />
        )}
      </div>

      {/* Monthly schedule info banner */}
      <div className={styles.infoBanner}>
        <SyncOutlined className={styles.infoBannerIcon} />
        <div className={styles.infoBannerText}>
          <strong>{t("ai_services.monthly_schedule_title")}</strong>
          <span>{t("ai_services.monthly_schedule_desc")}</span>
        </div>
        <Button
          type="link"
          size="small"
          icon={<HomeOutlined />}
          onClick={() => history.push("/console/my-workspace")}
          className={styles.infoBannerLink}
        >
          {t("ai_services.monthly_schedule_cta")}
        </Button>
      </div>

      {/* Quota Card */}
      {loading ? (
        <div className={styles.quotaCard}>
          <Skeleton active paragraph={{ rows: 2 }} />
        </div>
      ) : quota && (
        <div className={styles.quotaCard}>
          <div className={styles.quotaCardTop}>
            <div className={styles.quotaInfo}>
              <span className={styles.quotaLabel}>{t("ai_services.monthly_credits")}</span>
              <span className={styles.quotaNumbers}>
                <strong>{formatAppNumber(quota.used, i18n.language)}</strong>
                <span className={styles.quotaSep}>/</span>
                {formatAppNumber(quota.total, i18n.language)}
              </span>
            </div>
            <div className={styles.quotaMeta}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {t("ai_services.resets_in")} {quota.days_until_reset} {t("ai_services.days")}
            </div>
          </div>
          <Progress
            percent={quotaPercent}
            showInfo={false}
            strokeColor={quotaPercent > 85 ? "#ff4d4f" : quotaPercent > 60 ? "#faad14" : "#6c47d5"}
            trailColor="#f0f0f0"
            strokeWidth={8}
          />
          <div className={styles.quotaCardBottom}>
            <div className={styles.quotaBottomLeft}>
              <span>{formatAppNumber(quota.remaining, i18n.language)} {t("ai_services.credits_remaining")}</span>
              {quota.next_run_date && (
                <span className={styles.quotaResetDate}>
                  {t("ai_services.resets_on")}: {new Date(quota.next_run_date).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              )}
              <Link to="/settings/ai-credits" className={styles.creditHistoryLink}>
                {t("ai_credits_page.viewHistoryLink")}
              </Link>
            </div>
            <div className={styles.payPerUseToggle}>
              <Tooltip title={t("ai_services.pay_per_use_tooltip")}>
                <InfoCircleOutlined style={{ marginRight: 6, color: "#888" }} />
              </Tooltip>
              <Switch
                size="small"
                checked={payPerUseEnabled}
                onChange={setPayPerUseEnabled}
                disabled={formDisabled}
              />
              <span className={styles.payPerUseLabel}>{t("ai_services.pay_per_use_overage")}</span>
            </div>
          </div>
        </div>
      )}

      {/* Monthly AI source: brand group + dashboard (Welcome parity) */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderLeft}>
            <h2 className={styles.sectionTitle}>{t("ai_services.source_scope_title")}</h2>
          </div>
        </div>
        <p className={styles.sourceScopeHint}>{t("ai_services.source_scope_desc_nested")}</p>
        {!loading && brandGroups.length === 0 && (
          <Alert
            type="info"
            showIcon
            message={t("ai_services.brand_groups_empty_title")}
            description={t("ai_services.brand_groups_empty_desc")}
            className={styles.sourceScopeAlert}
          />
        )}
        <div className={styles.sourceScopeRow}>
          <div className={styles.sourceScopeField}>
            <span className={styles.sourceScopeLabel}>{t("ai_services.source_nested_label")}</span>
            <span className={styles.nestedHint}>{t("ai_services.source_nested_hint")}</span>
            <Cascader
              className={styles.sourceScopeCascader}
              popupClassName={styles.cascaderDropdownLight}
              options={cascaderOptions}
              loadData={loadCascaderData}
              value={cascaderValue}
              onChange={handleSourceCascaderChange}
              placeholder={t("ai_services.source_nested_placeholder")}
              allowClear
              showSearch={{
                filter(inputValue, path) {
                  return path.some((option) =>
                    String(option.label || "")
                      .toLowerCase()
                      .includes(String(inputValue || "").toLowerCase()),
                  );
                },
              }}
              changeOnSelect={false}
              disabled={formDisabled}
              displayRender={(labels) => labels.join(" → ")}
            />
          </div>
        </div>
      </div>

      {/* Pivot Key + Value Selection — single unified card */}
      <div className={styles.section}>
        {/* Pivot dimension selector */}
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderLeft}>
            <h2 className={styles.sectionTitle}>{t("ai_services.pivot_key_title")}</h2>
          </div>
        </div>

        {pivotOptionsLoading ? (
          <div className={styles.pivotLoadingBanner} role="status" aria-live="polite">
            <Spin indicator={<LoadingOutlined className={styles.pivotLoadingIcon} spin />} size="large" />
            <div className={styles.pivotLoadingCopy}>
              <strong className={styles.pivotLoadingTitle}>
                {t("ai_services.loading_pivot_banner_title")}
              </strong>
              <span className={styles.pivotLoadingDesc}>
                {t("ai_services.loading_pivot_banner_desc")}
              </span>
            </div>
          </div>
        ) : (
          <>
            <Select
              className={styles.pivotKeySelect}
              placeholder={t("ai_services.pivot_key_placeholder")}
              value={selectedPivotKey}
              onChange={handlePivotKeyChange}
              options={pivotKeyOptions}
              allowClear
              disabled={formDisabled}
            />
            {!selectedPivotKey && Object.keys(pivotOptions).length === 0 && (
              <p className={styles.noPivotHint}>{t("ai_services.no_pivot_keys")}</p>
            )}

            {/* Value list — shown inline when a dimension is selected */}
            {selectedPivotKey && (
              <div className={styles.vendorSubSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionHeaderLeft}>
                    <h2 className={styles.sectionTitle}>{t("ai_services.vendor_section_title")}</h2>
                    <Tag className={styles.countTag}>
                      {allVendors.length} {t("ai_services.vendors")} · {selectedVendorNames.length} {t("ai_services.selected")}
                    </Tag>
                  </div>
                  <div className={styles.sectionActions}>
                    <Button
                      size="small"
                      icon={<CheckSquareOutlined />}
                      onClick={handleSelectAllVendors}
                      disabled={formDisabled}
                    >
                      {t("selectAll")}
                    </Button>
                    <Button
                      size="small"
                      icon={<CloseSquareOutlined />}
                      onClick={handleClearVendors}
                      disabled={formDisabled}
                    >
                      {t("clearAll")}
                    </Button>
                  </div>
                </div>

                <Input
                  prefix={<SearchOutlined />}
                  placeholder={t("ai_services.search_vendors")}
                  value={vendorSearch}
                  onChange={(e) => {
                    setVendorSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className={styles.searchInput}
                  allowClear
                  disabled={formDisabled}
                />

                {selectedVendorNames.length > 0 && (
                  <div className={styles.selectedPivotValuesSummary}>
                    <span className={styles.selectedPivotValuesLabel}>
                      {t("ai_services.selected_values_heading")}
                    </span>
                    <div className={styles.selectedPivotValuesTags}>
                      {selectedVendorNames.map((name) => {
                        const inList = allVendors.some((v) => v.name === name);
                        return (
                          <Tooltip
                            key={name}
                            title={
                              inList
                                ? undefined
                                : t("ai_services.selected_value_not_in_list")
                            }
                          >
                            <Tag
                              className={
                                inList
                                  ? styles.selectedValueTag
                                  : styles.selectedValueTagOrphan
                              }
                              closable={!formDisabled}
                              onClose={(e) => {
                                e.preventDefault();
                                handleVendorToggle(name, false);
                              }}
                            >
                              {name}
                            </Tag>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className={styles.brandList}>
                  {pagedVendors.length === 0 ? (
                    <div className={styles.emptyBrands}>{t("noData")}</div>
                  ) : (
                    pagedVendors.map((vendor) => {
                      const isChecked = selectedVendorNames.includes(vendor.name);
                      return (
                        <div
                          key={vendor.name}
                          className={`${styles.brandRow} ${isChecked ? styles.brandRowSelected : ""}`}
                          onClick={() =>
                            !formDisabled &&
                            handleVendorToggle(vendor.name, !isChecked)
                          }
                        >
                          <Checkbox
                            checked={isChecked}
                            disabled={formDisabled}
                            onChange={(e) => handleVendorToggle(vendor.name, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className={styles.brandName}>{vendor.name}</span>
                        </div>
                      );
                    })
                  )}
                  {filteredVendors.length > pagedVendors.length && (
                    <div className={styles.loadMoreRow}>
                      <Button
                        type="link"
                        onClick={() => setCurrentPage((p) => p + 1)}
                        disabled={formDisabled}
                      >
                        {t("ai_services.load_more")} ({filteredVendors.length - pagedVendors.length} {t("ai_services.more")})
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Topics Selection */}
      {availableTopics.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionHeaderLeft}>
              <h2 className={styles.sectionTitle}>{t("ai_services.topics_section_title")}</h2>
              {selectedTopicIds.length > 0 && (
                <Tag className={styles.countTag}>
                  {selectedTopicIds.length} {t("ai_services.selected")}
                </Tag>
              )}
            </div>
          </div>
          <div className={styles.metricsGrid}>
            {availableTopics.map((topic) => {
              const isChecked = selectedTopicIds.includes(topic.id);
              return (
                <div
                  key={topic.id}
                  className={`${styles.metricCard} ${isChecked ? styles.metricCardSelected : ""}`}
                  onClick={() =>
                    !formDisabled &&
                    handleTopicToggle(topic.id, !isChecked)
                  }
                >
                  <Checkbox
                    checked={isChecked}
                    disabled={formDisabled}
                    onChange={(e) => handleTopicToggle(topic.id, e.target.checked)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className={styles.metricName}>{topic.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Metrics Selection */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>{t("ai_services.metrics_section_title")}</h2>
        </div>
        <div className={styles.metricsGrid}>
          {sortedMetrics.map((metric) => {
            const isChecked = selectedMetricIds.includes(metric.id);
            const billable = AI_CREDIT_METRIC_IDS.has(Number(metric.id));
            return (
              <div
                key={metric.id}
                className={`${styles.metricCard} ${isChecked ? styles.metricCardSelected : ""}`}
                onClick={() =>
                  !formDisabled &&
                  handleMetricToggle(metric.id, !isChecked)
                }
              >
                <Checkbox
                  checked={isChecked}
                  disabled={formDisabled}
                  onChange={(e) => handleMetricToggle(metric.id, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className={styles.metricCardMain}>
                  <AiMonthlyMetricIcon metricId={metric.id} className={styles.metricTitleIcon} />
                  <span className={styles.metricName}>{aiMetricCardLabel(metric, t)}</span>
                  <Tooltip
                    title={
                      billable
                        ? t("ai_services.metric_badge_genai_tooltip")
                        : t("ai_services.metric_badge_kpi_tooltip")
                    }
                  >
                    <span
                      className={styles.metricKindBadge}
                      data-kind={billable ? "genai" : "kpi"}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {billable
                        ? t("ai_services.metric_badge_genai")
                        : t("ai_services.metric_badge_kpi")}
                    </span>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>

        {selectedVendorNames.length > 0 && selectedMetricIds.length > 0 && (
          <div className={`${styles.creditCalc} ${quotaOverBudget ? styles.creditCalcWarning : ""}`}>
            <ThunderboltOutlined style={{ marginRight: 6 }} />
            {selectedVendorNames.length} {t("ai_services.vendors")} × {billableMetricCount}{" "}
            {t("ai_services.credit_billed_metrics")} ={" "}
            <strong>{creditsPerMonth}</strong> {t("ai_services.credits_per_month")}
            {quotaOverBudget && (
              <span className={styles.overBudgetBadge}>
                {t("ai_services.exceeds_quota")}
              </span>
            )}
          </div>
        )}

        {/* Schedule */}
        <div className={styles.scheduleSection}>
          <div className={styles.scheduleSectionHeader}>
            <ClockCircleOutlined style={{ marginRight: 6 }} />
            {t("ai_services.schedule_title")}
            {!loading && isDirty && (
              <span className={`${styles.runStatusBadge} ${styles.runStatus_pending}`}>
                {t("ai_services.run_status_schedule_changed")}
              </span>
            )}
            {!loading && !isDirty && lastRunStatus && (
              <span className={`${styles.runStatusBadge} ${styles[`runStatus_${lastRunStatus}`]}`}>
                {t(`ai_services.run_status_${lastRunStatus}`)}
                {lastRunMonth && ` · ${dayjs(lastRunMonth).format("MMM YYYY")}`}
                {lastRunStatus === "completed" && (
                  <Button
                    type="link"
                    size="small"
                    icon={<RightOutlined />}
                    style={{ padding: "0 0 0 6px", height: "auto", fontSize: 12 }}
                    onClick={() => history.push("/console/ai-services/auto-reports")}
                  >
                    {t("ai_services.go_to_reports")}
                  </Button>
                )}
                {lastRunAt && lastRunStatus === "in_progress" && (
                  <span className={styles.runStatusTime}> · {(() => {
                    const diffMs = dayjs().diff(dayjs.utc(lastRunAt));
                    const totalMin = Math.floor(diffMs / 60000);
                    const hrs = Math.floor(totalMin / 60);
                    const mins = totalMin % 60;
                    if (hrs > 0 && mins > 0) return `${hrs} ${t("ai_services.hours_abbr")} ${mins} ${t("ai_services.minutes_abbr")} ${t("ai_services.ago")}`;
                    if (hrs > 0) return `${hrs} ${t("ai_services.hours_abbr")} ${t("ai_services.ago")}`;
                    if (totalMin > 0) return `${totalMin} ${t("ai_services.minutes_abbr")} ${t("ai_services.ago")}`;
                    return t("ai_services.just_now");
                  })()}</span>
                )}
              </span>
            )}
          </div>
          <p className={styles.scheduleDesc}>
            {scheduleMode === "one_time"
              ? t("ai_services.schedule_desc_one_time")
              : t("ai_services.schedule_desc")}
          </p>
          {scheduleMode === "one_time" && oneTimePending && !isDirty && (
            <Alert
              type="info"
              showIcon
              className={styles.oneTimePendingAlert}
              message={t("ai_services.one_time_pending_banner")}
            />
          )}
          {loading ? (
            <Skeleton active paragraph={{ rows: 2 }} style={{ marginTop: 8 }} />
          ) : (
          <>
          <div className={styles.scheduleModeGrid}>
            <button
              type="button"
              className={`${styles.scheduleModeCard} ${
                scheduleMode === "monthly" ? styles.scheduleModeCardActive : ""
              }`}
              onClick={() => !formDisabled && setScheduleMode("monthly")}
              disabled={formDisabled}
              aria-pressed={scheduleMode === "monthly"}
            >
              <CalendarOutlined className={styles.scheduleModeIcon} />
              <span className={styles.scheduleModeTitle}>
                {t("ai_services.schedule_mode_monthly")}
              </span>
              <span className={styles.scheduleModeHint}>
                {t("ai_services.schedule_mode_monthly_hint")}
              </span>
            </button>
            <button
              type="button"
              className={`${styles.scheduleModeCard} ${
                scheduleMode === "one_time" ? styles.scheduleModeCardActive : ""
              }`}
              onClick={() => !formDisabled && setScheduleMode("one_time")}
              disabled={formDisabled}
              aria-pressed={scheduleMode === "one_time"}
            >
              <FieldTimeOutlined className={styles.scheduleModeIcon} />
              <span className={styles.scheduleModeTitle}>
                {t("ai_services.schedule_mode_one_time")}
              </span>
              <span className={styles.scheduleModeHint}>
                {t("ai_services.schedule_mode_one_time_hint")}
              </span>
            </button>
          </div>
          {scheduleMode === "one_time" ? (
            <div className={styles.scheduleOneTimeFields}>
              <div className={styles.scheduleRow}>
                <label className={styles.scheduleLabel}>
                  {t("ai_services.one_time_feedback_range")}
                </label>
                <RangePicker
                  className={styles.oneTimeRangePicker}
                  value={
                    oneTimeSince && oneTimeUntil
                      ? [dayjs(oneTimeSince), dayjs(oneTimeUntil)]
                      : null
                  }
                  onChange={(vals) => {
                    if (!vals || vals.length !== 2) {
                      setOneTimeSince(null);
                      setOneTimeUntil(null);
                      return;
                    }
                    setOneTimeSince(vals[0].format("YYYY-MM-DD"));
                    setOneTimeUntil(vals[1].format("YYYY-MM-DD"));
                  }}
                  disabled={formDisabled}
                  allowClear
                />
              </div>
              <div className={styles.scheduleRunRow}>
                <label className={styles.scheduleLabel}>
                  {t("ai_services.one_time_run_date")}
                </label>
                <DatePicker
                  className={styles.oneTimeRunDatePicker}
                  value={oneTimeRunDate ? dayjs(oneTimeRunDate) : null}
                  onChange={(val) =>
                    setOneTimeRunDate(val ? val.format("YYYY-MM-DD") : null)
                  }
                  disabled={formDisabled}
                  allowClear
                />
                <div className={styles.scheduleTimeGroup}>
                  <label className={styles.scheduleLabel}>
                    {t("ai_services.schedule_time")}
                  </label>
                  <Select
                    value={dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).hour()}
                    onChange={(h) => {
                      const localMinute = dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).minute();
                      const asUtc = dayjs().tz(userIanaTz).hour(h).minute(localMinute).second(0).utc();
                      setScheduledHour(asUtc.hour());
                      setScheduledMinute(asUtc.minute());
                    }}
                    className={styles.scheduleTimeSelect}
                    disabled={formDisabled}
                    options={Array.from({ length: 24 }, (_, i) => ({
                      value: i,
                      label: String(i).padStart(2, "0"),
                    }))}
                  />
                  <span className={styles.scheduleTimeSep}>:</span>
                  <Select
                    value={Math.round(dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).minute() / 5) * 5}
                    onChange={(m) => {
                      const localHour = dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).hour();
                      const asUtc = dayjs().tz(userIanaTz).hour(localHour).minute(m).second(0).utc();
                      setScheduledHour(asUtc.hour());
                      setScheduledMinute(asUtc.minute());
                    }}
                    className={styles.scheduleTimeSelect}
                    disabled={formDisabled}
                    options={Array.from({ length: 12 }, (_, i) => ({
                      value: i * 5,
                      label: String(i * 5).padStart(2, "0"),
                    }))}
                  />
                </div>
              </div>
            </div>
          ) : (
          <div className={styles.scheduleRow}>
            <label className={styles.scheduleLabel}>{t("ai_services.schedule_day")}</label>
            <Select
              value={scheduledDay}
              onChange={(v) => setScheduledDay(v)}
              className={styles.scheduleDaySelect}
              disabled={formDisabled}
              options={[
                ...Array.from({ length: 28 }, (_, i) => ({
                  value: i + 1,
                  label: `${i + 1}`,
                })),
                { value: 31, label: t("ai_services.last_day_of_month") },
              ]}
            />
            <label className={styles.scheduleLabel}>{t("ai_services.schedule_time")}</label>
            <Select
              value={dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).hour()}
              onChange={(h) => {
                const localMinute = dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).minute();
                const asUtc = dayjs().tz(userIanaTz).hour(h).minute(localMinute).second(0).utc();
                setScheduledHour(asUtc.hour());
                setScheduledMinute(asUtc.minute());
              }}
              className={styles.scheduleTimeSelect}
              disabled={formDisabled}
              options={Array.from({ length: 24 }, (_, i) => ({
                value: i,
                label: String(i).padStart(2, "0"),
              }))}
            />
            <span className={styles.scheduleTimeSep}>:</span>
            <Select
              value={Math.round(dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).minute() / 5) * 5}
              onChange={(m) => {
                const localHour = dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).hour();
                const asUtc = dayjs().tz(userIanaTz).hour(localHour).minute(m).second(0).utc();
                setScheduledHour(asUtc.hour());
                setScheduledMinute(asUtc.minute());
              }}
              className={styles.scheduleTimeSelect}
              disabled={formDisabled}
              options={Array.from({ length: 12 }, (_, i) => ({
                value: i * 5,
                label: String(i * 5).padStart(2, "0"),
              }))}
            />
          </div>
          )}
          <div className={styles.scheduleExplain}>
            {scheduleMode === "one_time" ? (
              <>
                <div className={styles.scheduleExplainRow}>
                  <ClockCircleOutlined className={styles.scheduleExplainIcon} aria-hidden />
                  <div className={styles.scheduleExplainBody}>
                    <span className={styles.scheduleExplainTitle}>
                      {t("ai_services.schedule_explain_timing_title")}
                    </span>
                    <span className={styles.scheduleExplainText}>
                      {oneTimeRunDate
                        ? t("ai_services.schedule_explain_one_time_timing", {
                            date: dayjs(oneTimeRunDate).format("D MMMM YYYY"),
                            time: scheduleTimeLabel,
                          })
                        : t("ai_services.schedule_explain_one_time_timing_missing")}
                    </span>
                  </div>
                </div>
                <div className={styles.scheduleExplainRow}>
                  <MessageOutlined className={styles.scheduleExplainIcon} aria-hidden />
                  <div className={styles.scheduleExplainBody}>
                    <span className={styles.scheduleExplainTitle}>
                      {t("ai_services.schedule_explain_data_title")}
                    </span>
                    <span className={styles.scheduleExplainText}>
                      {oneTimeSince && oneTimeUntil
                        ? t("ai_services.schedule_explain_one_time_data", {
                            since: dayjs(oneTimeSince).format("D MMMM YYYY"),
                            until: dayjs(oneTimeUntil).format("D MMMM YYYY"),
                          })
                        : t("ai_services.schedule_explain_one_time_data_missing")}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
            <div className={styles.scheduleExplainRow}>
              <ClockCircleOutlined className={styles.scheduleExplainIcon} aria-hidden />
              <div className={styles.scheduleExplainBody}>
                <span className={styles.scheduleExplainTitle}>
                  {t("ai_services.schedule_explain_timing_title")}
                </span>
                <span className={styles.scheduleExplainText}>
                  {scheduledDay === 31
                    ? t("ai_services.schedule_explain_timing_last", { time: scheduleTimeLabel })
                    : t("ai_services.schedule_explain_timing_day", {
                        day: scheduledDay,
                        time: scheduleTimeLabel,
                      })}
                </span>
              </div>
            </div>
            <div className={styles.scheduleExplainRow}>
              <MessageOutlined className={styles.scheduleExplainIcon} aria-hidden />
              <div className={styles.scheduleExplainBody}>
                <span className={styles.scheduleExplainTitle}>
                  {t("ai_services.schedule_explain_data_title")}
                </span>
                <span className={styles.scheduleExplainText}>
                  {t("ai_services.schedule_range_info", {
                    prevMonthStart: scheduleCoveragePreview.prevMonthStart,
                    prevMonthEnd: scheduleCoveragePreview.prevMonthEnd,
                  })}
                </span>
              </div>
            </div>
              </>
            )}
          </div>
          </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={styles.pageFooter}>
        {quota && (
          <span className={styles.footerMeta}>
            {t("ai_services.footer_this_month")}: {quota.used} {t("ai_services.credits_used")} · {creditsPerMonth} {t("ai_services.scheduled")} ·{" "}
            {scheduleMode === "one_time"
              ? oneTimeRunDate
                ? t("ai_services.schedule_preview_one_time", {
                    date: dayjs(oneTimeRunDate).format("D MMM YYYY"),
                    time: dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).format("HH:mm"),
                  })
                : t("ai_services.schedule_mode_one_time")
              : scheduledDay === 31
              ? t("ai_services.schedule_preview_last_day", {
                  time: dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).format("HH:mm"),
                })
              : t("ai_services.schedule_preview", {
                  day: scheduledDay,
                  time: dayjs.utc().hour(scheduledHour).minute(scheduledMinute).second(0).tz(userIanaTz).format("HH:mm"),
                })}
          </span>
        )}
        <div className={styles.footerActions}>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={formDisabled}
            icon={<SaveOutlined />}
            className={styles.saveBtn}
          >
            {t("ai_services.save_settings")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AiAutoRefreshSettings;
